/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { CSSObjectWithLabel } from 'react-select';
import { getInitiativeUsers } from '../../../actions/initiative';
import { UserWithStats } from '../../../api/initiative-stats';
import { UserMin } from '../../../constants/users';
import { useAppSelector } from '../../../reducers';
import Loader from '../../loader';
import { MessageFormProps, MessageFormField } from '../types';
import { DestinationCheckboxFields } from './DestinationCheckboxFields';
import { MessageField } from './MessageField';
import { SubjectField } from './SubjectField';
import { UserSelectField } from './UserSelectField';

interface UserMessageFormProps extends MessageFormProps {
  userIds: string[];
  users?: UserWithStats[];
}

const customStyles = {
  indicatorsContainer: () => ({
    display: 'none',
  }),
  multiValueRemove: () => ({
    display: 'none',
  }),
  multiValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    paddingRight: '3px',
  }),
};

export const UserMessageForm = (props: UserMessageFormProps) => {
  const { onChange, form, users = [] } = props;

  const [usersState, setUsers] = useState<{ users: UserMin[]; loaded: boolean }>({ users: [], loaded: false });
  const initiativeId = useAppSelector((state) => (state.initiative.loaded ? state.initiative.data._id : undefined));

  useEffect(() => {
    if (!initiativeId || usersState.loaded) {
      return;
    }
    if (users.length) {
      return setUsers({ users, loaded: true });
    }
    getInitiativeUsers(initiativeId)
      .then((data: { users: UserMin[] }) => {
        setUsers({ users: data.users, loaded: true });
      })
      .catch((e) => setUsers({ users: [], loaded: true }));
  }, [initiativeId, users, usersState.loaded]);

  if (!usersState.loaded) {
    return <Loader />;
  }

  const selectProps = {
    isSearchable: false,
    openMenuOnClick: false,
    styles: customStyles,
  };

  return (
    <div className='d-flex flex-column'>
      <UserSelectField
        users={usersState.users}
        userIds={form.userIds}
        onChange={onChange(MessageFormField.UserIds)}
        selectProps={selectProps}
      />
      <SubjectField subject={form.subject} onChange={onChange(MessageFormField.Subject)} />
      <MessageField message={form.message} onChange={onChange(MessageFormField.Message)} />
      <DestinationCheckboxFields sendByEmail={form.sendByEmail} onChange={onChange(MessageFormField.SendByEmail)} />
    </div>
  );
};
